import type { ReactNode } from 'react';
import { EmptyState, ErrorState, Loading } from './States.js';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string;
  /** Make the header a sort toggle. Emits `sortKey` (or `key`) via onSortChange. */
  sortable?: boolean;
  sortKey?: string;
  /** Render a filter control beneath the header. Emits via onFilterChange. */
  filterable?: boolean;
  filterKey?: string;
  filterType?: 'text' | 'select';
  filterOptions?: { value: string; label: string }[];
  filterPlaceholder?: string;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface PaginationState {
  page: number; // zero-based
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  /** Controlled sort state + handler. Column must set `sortable`. */
  sort?: SortState;
  onSortChange?: (key: string) => void;
  /** Controlled per-column filter values keyed by `filterKey`/`key`. */
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  pagination?: PaginationState;
}

function SortIndicator({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <span className="th-sort__icon th-sort__icon--idle">↕</span>;
  return <span className="th-sort__icon">{dir === 'asc' ? '▲' : '▼'}</span>;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  isError,
  error,
  onRetry,
  onRowClick,
  emptyMessage,
  sort,
  onSortChange,
  filterValues,
  onFilterChange,
  pagination,
}: DataTableProps<T>) {
  const hasFilterRow = Boolean(onFilterChange) && columns.some((c) => c.filterable);
  const interactive = hasFilterRow || Boolean(pagination) || Boolean(onSortChange);

  // Non-interactive callers keep the original centered states (unchanged look).
  if (!interactive) {
    if (isLoading) return <Loading />;
    if (isError) return <ErrorState error={error} onRetry={onRetry} />;
    if (!rows || rows.length === 0) {
      return <EmptyState message={emptyMessage ?? 'No records found.'} />;
    }
  }

  const empty = !rows || rows.length === 0;

  return (
    <div className="table-block">
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((c) => {
                const sortKey = c.sortKey ?? c.key;
                const active = sort?.key === sortKey;
                return (
                  <th key={c.key} style={c.width ? { width: c.width } : undefined}>
                    {c.sortable && onSortChange ? (
                      <button
                        type="button"
                        className={`th-sort${active ? ' th-sort--active' : ''}`}
                        onClick={() => onSortChange(sortKey)}
                        aria-label={`Sort by ${typeof c.header === 'string' ? c.header : c.key}`}
                      >
                        <span>{c.header}</span>
                        <SortIndicator active={active} dir={active ? sort!.dir : 'asc'} />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
            {hasFilterRow && (
              <tr className="data-table__filter-row">
                {columns.map((c) => {
                  const fKey = c.filterKey ?? c.key;
                  const value = filterValues?.[fKey] ?? '';
                  return (
                    <th key={c.key}>
                      {c.filterable && c.filterType === 'select' ? (
                        <select
                          className="th-filter"
                          value={value}
                          onChange={(e) => onFilterChange!(fKey, e.target.value)}
                          aria-label={`Filter by ${c.key}`}
                        >
                          <option value="">All</option>
                          {c.filterOptions?.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : c.filterable ? (
                        <input
                          className="th-filter"
                          type="search"
                          value={value}
                          placeholder={c.filterPlaceholder ?? 'Filter…'}
                          onChange={(e) => onFilterChange!(fKey, e.target.value)}
                          aria-label={`Filter by ${c.key}`}
                        />
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="data-table__state">
                  <Loading />
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={columns.length} className="data-table__state">
                  <ErrorState error={error} onRetry={onRetry} />
                </td>
              </tr>
            ) : empty ? (
              <tr>
                <td colSpan={columns.length} className="data-table__state">
                  <EmptyState message={emptyMessage ?? 'No records found.'} />
                </td>
              </tr>
            ) : (
              rows!.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? 'data-table__row--clickable' : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key}>{c.render(row)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && <Pagination {...pagination} />}
    </div>
  );
}

function Pagination({ page, pageSize, total, onPageChange }: PaginationState) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const first = total === 0 ? 0 : current * pageSize + 1;
  const last = Math.min(total, (current + 1) * pageSize);
  const canPrev = current > 0;
  const canNext = current < pageCount - 1;

  return (
    <div className="pagination">
      <span className="pagination__info">
        {total === 0 ? 'No records' : `${first}–${last} of ${total}`}
      </span>
      <div className="pagination__controls">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onPageChange(0)}
          disabled={!canPrev}
          aria-label="First page"
        >
          «
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onPageChange(current - 1)}
          disabled={!canPrev}
          aria-label="Previous page"
        >
          ‹ Prev
        </button>
        <span className="pagination__page">
          Page {current + 1} of {pageCount}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onPageChange(current + 1)}
          disabled={!canNext}
          aria-label="Next page"
        >
          Next ›
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onPageChange(pageCount - 1)}
          disabled={!canNext}
          aria-label="Last page"
        >
          »
        </button>
      </div>
    </div>
  );
}
